import type { ReportMetric } from "./report-metrics";
import type { SeriesPeriod } from "./series-period";

/**
 * `piracy-audience-series` v1 — an audience time series with the periods during
 * which blocking ran.
 *
 * Audiences are concurrent viewers *sampled* per bucket (gauges: never summed
 * across buckets). A blocking interval says when blocking was requested; it is
 * an annotation, not evidence of its effect.
 */
export const PIRACY_AUDIENCE_SERIES_CONTRACT = {
  name: "piracy-audience-series",
  version: 1,
} as const;

export interface PiracyAudiencePoint {
  /** ISO 8601 UTC start of the source bucket (five minutes for PAM). */
  timestamp: string;
  /** Viewers of sources that can be blocked safely. */
  safeToBlock: number;
  /** Viewers of sources where blocking is risky. */
  unsafeToBlock: number;
  /** Reference traffic seen by the Cortex/GVA feed; `null` when it has no sample. */
  observed: number | null;
  /**
   * Counterfactual pirate audience had blocking not run this bucket. Optional:
   * only a source that can compute this (e.g. a closed reporting period) sets
   * it; omitted entirely, the chart draws three series instead of four. Kept
   * on the shared contract rather than a second widget — see "Reuse before
   * creating a widget" in `WIDGET_AUTHORING.md`.
   */
  estimated?: number | null;
}

/**
 * Optional business-impact summary a report snapshot can attach to the same
 * series. A live source never sets this; when present, the widget renders it
 * as a second stat row above the chart.
 */
export interface PiracyAudienceReportMetrics {
  avgApproxAudience: number;
  avgEstimatedAudience: number;
  /** 0–1 fraction, e.g. 0.56 for a 56% reduction. */
  audienceReductionPct: number;
  revenueOpportunities: number;
}

/** `active` while blocking still runs (then `to` is the data-through instant). */
export type BlockingIntervalStatus = "active" | "completed";

export interface BlockingInterval {
  from: string;
  to: string;
  /** Unknown statuses are kept and shown as text. */
  status: BlockingIntervalStatus | (string & {});
  provider: string | null;
}

export interface PiracyAudienceSeriesData {
  points: PiracyAudiencePoint[];
  blockingIntervals: BlockingInterval[];
  blockingPeriods?: BlockingPeriod[];
  /**
   * `false` when the blocking periods ran elsewhere: none reaches the selected
   * countries. Omitted or `null` when the source cannot tell.
   */
  blockingApplies?: boolean | null;
  freshness: PiracyAudienceFreshness | null;
  period?: SeriesPeriod | null;
  /** Present only for sources that carry a report's business-impact figures. */
  reportMetrics?: PiracyAudienceReportMetrics | null;
  /**
   * The report's figures for the same scope and period (those of the piracy
   * audience indicator), only the ones the widget asked for (`metrics` field
   * and metric IDs); absent when none was asked.
   */
  metrics?: ReportMetric[];
}

export interface BlockingPeriod {
  id: string;
  from: string;
  to: string;
  status: BlockingIntervalStatus | (string & {});
  viewersDisrupted?: number | null;
}

/** How far the data goes and where it comes from. Structurally the same as the report freshness of `metric-kpi`. */
export interface PiracyAudienceFreshness {
  generatedAt: string;
  dataThrough: string;
  /** "demo", "clickhouse", or a named upstream feed. */
  source: string;
  isEstimated: boolean;
}
